import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { VideoItem } from "@/types/video";

interface HeroCarouselProps {
  items: VideoItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStart = useRef<number | null>(null);

  useEffect(() => {
    if (items.length <= 1) return undefined;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 5200);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  const go = (next: number) => setIndex((next + items.length) % items.length);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStart.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(diff) > 50) {
      go(diff > 0 ? index - 1 : index + 1);
    }
    touchStart.current = null;
  };

  return (
    <section
      className="relative w-full h-[420px] md:h-[600px] overflow-hidden bg-primary-950"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-label="影视推荐轮播"
    >
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100 z-10" : "opacity-0 z-0"
          }`}
        >
          <img
            src={item.banner || item.cover}
            alt={`${item.title}焦点图`}
            title={`${item.title} - 51无敌电影网`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-foreground-950/20" />
        </div>
      ))}

      <div className="relative z-20 w-full h-full px-4 md:px-6 flex items-center">
        <div className="max-w-xl">
          <div className="flex items-center gap-2 mb-4 animate-fade-up">
            <span className="px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-label font-semibold">
              最新热映
            </span>
            <span className="px-2.5 py-1 rounded-md bg-background-50/15 text-background-50 text-xs font-label backdrop-blur-sm">
              {items[index].type === "movie" ? "电影" : "电视剧"}
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 leading-tight animate-fade-up animate-delay-1">
            {items[index].title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-100 animate-fade-up animate-delay-2">
            <span className="flex items-center gap-1 text-accent-400 font-label font-semibold">
              <i className="ri-star-fill"></i>
              {items[index].rating.toFixed(1)}
            </span>
            <span>{items[index].year}</span>
            <span>{items[index].region}</span>
            <span>{items[index].genres.slice(0, 3).join(" / ")}</span>
          </div>
          <p className="mt-4 text-sm md:text-base text-background-200 leading-relaxed line-clamp-2 md:line-clamp-3 animate-fade-up animate-delay-3">
            {items[index].synopsis}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3 animate-fade-up animate-delay-4">
            <Link
              to={`/detail/${items[index].id}`}
              className="px-6 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-semibold flex items-center gap-2 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-fill text-lg"></i>
              立即观看
            </Link>
            <Link
              to={`/detail/${items[index].id}`}
              className="px-6 py-2.5 rounded-md bg-background-50/15 hover:bg-background-50/25 text-background-50 text-sm font-label font-semibold flex items-center gap-2 transition-colors backdrop-blur-sm cursor-pointer whitespace-nowrap"
            >
              <i className="ri-information-line text-lg"></i>
              查看详情
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(index - 1)}
        aria-label="上一张"
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-background-50/15 hover:bg-background-50/30 text-background-50 items-center justify-center transition-colors backdrop-blur-sm cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        aria-label="下一张"
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-background-50/15 hover:bg-background-50/30 text-background-50 items-center justify-center transition-colors backdrop-blur-sm cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            onClick={() => go(i)}
            aria-label={`切换到第${i + 1}张`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-8 bg-accent-500" : "w-4 bg-background-50/50 hover:bg-background-50/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}